import React from 'react';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts/highstock';

import Utility from '../../../../util/utilityfunctions';
import {labels} from '../../../../util/utils';

import OutputText from '@ey/canvascoreservices/OutputText';
import StyledPieChart from './StyledPieChart';
import Legend from '../Legend';

const PieChart = (props) => {
	const numberFormat = useSelector(({userPreferences}) => userPreferences.numberFormat);

	const options = {
		chart: {
			type: 'pie',
			backgroundColor: 'transparent',
			height: props.chartHeight ? props.chartHeight : 240,
			width: props.chartHeight ? props.chartHeight : 240,
			events: {
				redraw: function() {
					//if I have declared onOverdueClick, I must link it to the click of the overdue label
					if (props.onOverdueClick) {
						const waiter = setTimeout(() => {
							clearTimeout(waiter);
							document
								.getElementById(props.graphicContainerId)
								?.getElementsByClassName('overdue-number-item')
								?.forEach((overdueLabel) =>
									overdueLabel.addEventListener('click', (event) => {
										const sender = event.target;
										props.onOverdueClick(`${sender.dataset.name} ${labels.overdue}`);
									})
								);
						}, 5);
					}
				}
			}
		},
		title: {
			text: null
		},
		credits: {
			enabled: false
		},
		navigation: {
			buttonOptions: {
				enabled: false
			}
		},
		legend: {
			enabled: false
		},
		tooltip: {
			enabled: true,
			outside: true,
			formatter: function() {
				return `<span class="name" style="font-size: 1.2rem; z-index: 100">${
					this.point.name
				}</span> | <span class="number-item">${Utility.getFormattedNumber(numberFormat, this.point.totalCount)} (${
					this.y
				}%)</span>`;
			}
		},
		plotOptions: {
			pie: {
				showInLegend: true,
				allowPointSelect: true,
				borderWidth: 2,
				borderColor: 'var(--neutrals-25)',
				cursor: 'pointer',
				dataLabels: {
					enabled: false
				},
				size: '110%'
			},
			series: {
				point: {
					events: {
						click: (e) => {
							props.onGraphicClick && props.onGraphicClick(e);
						}
					}
				}
			}
		},
		series: [
			{
				data: props.data,
				innerSize: '80%' // convert pie into donut
			}
		]
	};

	const renderTotalValue = () => {
		const text = Utility.getFormattedNumber(numberFormat, props.total);

		return (
			<OutputText h2 ellipsis title={text}>
				{text}
			</OutputText>
		);
	};

	const renderLegend = ({name, totalCount, totalOverdue, y}) => (
		<>
			<OutputText body3>{`${Utility.getFormattedNumber(numberFormat, totalCount)} (${y}%)`}</OutputText>
			<OutputText body3 light className="grey">
				{name}
			</OutputText>
			{!!totalOverdue && (
				<OutputText body3 light className="red">{`${Utility.getFormattedNumber(numberFormat, totalOverdue)} ${
					labels.taskStatusOverdue
				}`}</OutputText>
			)}
		</>
	);

	return (
		<StyledPieChart
			className="pie-chart-wrap"
			id={props.graphicContainerId}
			legendWidth={props.legendWidth}
			labelCenterOffset={props.labelCenterOffset}
		>
			<div className="pie">
				<div className="total">
					{renderTotalValue()}
					<OutputText body3 light ellipsis title={props.totalText}>
						{props.totalText}
					</OutputText>
				</div>
				<HighchartsReact highcharts={Highcharts} options={options} />
			</div>
			<Legend items={props.data} renderItem={renderLegend} />
		</StyledPieChart>
	);
};

PieChart.defaultProps = {
	data: [],
	total: 0,
	totalText: '',
	colors: [],
	userPreferences: {
		numberFormat: '0'
	},
	noResultMessageHeader: '',
	noResultMessageSubHeader: '',
	legendWidth: 200,
	labelCenterOffset: 0,
	onGraphicClick: () => {},
	graphicContainerId: window.crypto.randomUUID()
};

PieChart.propTypes = {
	data: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string,
			y: PropTypes.number, //Percent in pie.
			totalCount: PropTypes.number, //total number of that percent.
			totalOverdue: PropTypes.number //Extra info in the graph, not in the pie.
		})
	),
	userPreferences: PropTypes.shape({
		numberFormat: PropTypes.string
	}),
	total: PropTypes.number,
	totalText: PropTypes.string,
	colors: PropTypes.arrayOf(PropTypes.string),
	noResultMessageHeader: PropTypes.string,
	noResultMessageSubHeader: PropTypes.string,
	legendWidth: PropTypes.number,
	labelCenterOffset: PropTypes.number,
	onGraphicClick: PropTypes.func,
	onOverdueClick: PropTypes.func,
	graphicContainerId: PropTypes.string
};

export default PieChart;
